/** Where one currency's rate came from, so the page can say so honestly. */
export interface RateOrigin {
  /**
   * `ecb`: the European Central Bank's daily reference rate, via Frankfurter.
   * `exchangerate-api`: ExchangeRate-API's open feed, for currencies the ECB doesn't publish.
   * `peg`: fixed to `anchor` by its central bank, derived from the anchor's own rate.
   * `snapshot`: the bundled estimate below, used only when the live sources fail.
   */
  source: 'ecb' | 'exchangerate-api' | 'peg' | 'snapshot';
  /** ISO date the rate is from. For a peg, the anchor's date. */
  asOf: string;
  /**
   * Pegs only: the currency it's fixed to, and where that currency's rate
   * came from. No `origin` when the anchor is your own currency, which makes
   * the rate exact.
   */
  anchor?: { code: string; perAnchor: number; origin?: RateOrigin };
}

export interface FxRates {
  /** The currency all `rates` are quoted against 1 unit of. */
  base: string;
  rates: Record<string, number>;
  /** Per currency, where its rate came from. */
  origins: Record<string, RateOrigin>;
  /** The date of the main (ECB) quote, or of the snapshot if that failed. */
  asOf: string;
  /** False when nothing live could be loaded and every rate is the bundled snapshot. */
  isLive: boolean;
}

/**
 * A small bundled snapshot, GBP-based, refreshed by hand now and then. Only
 * used when the live sources can't be reached, and the page labels any rate
 * taken from it as an estimate with this date.
 */
const FALLBACK_ASOF = '2026-10-04';
const FALLBACK_GBP_RATES: Record<string, number> = {
  GBP: 1,
  EUR: 1.18,
  USD: 1.32,
  PLN: 5.14,
  RON: 6.26,
  HUF: 433,
  CHF: 1.1,
  SEK: 13.3,
  NOK: 12.7,
  DKK: 8.79,
  CAD: 1.88,
  AUD: 1.9,
  NZD: 2.36,
  JPY: 209,
  ZAR: 22,
  THB: 44.4,
  MYR: 5.4,
  PHP: 82.7,
  CRC: 605,
  MXN: 24.1,
  TRY: 65,
  AED: 4.86,
  CNY: 8.9,
  KRW: 1784,
  SAR: 4.96,
  INR: 127,
  VND: 34_330,
  IDR: 23_640,
  EGP: 69.1,
  PYG: 7_730,
  PEN: 4.56,
};

/**
 * Currencies held at a fixed rate to another by their central bank. Rather
 * than snapshot them (and let them go stale), they're derived from their
 * anchor on every quote — so they stay live whenever the anchor is.
 */
const PEGS: Record<string, { anchor: string; perAnchor: number }> = {
  BBD: { anchor: 'USD', perAnchor: 2 },
};

function withPegs(rates: FxRates): FxRates {
  const out = { ...rates, rates: { ...rates.rates }, origins: { ...rates.origins } };
  for (const [code, { anchor, perAnchor }] of Object.entries(PEGS)) {
    const anchorRate = out.rates[anchor];
    if (anchorRate === undefined || code === out.base) continue;
    out.rates[code] = anchorRate * perAnchor;
    const origin = anchor === out.base ? undefined : out.origins[anchor];
    out.origins[code] = { source: 'peg', asOf: origin?.asOf ?? out.asOf, anchor: { code: anchor, perAnchor, origin } };
  }
  return out;
}

const cache = new Map<string, Promise<FxRates>>();

/** Rates for 1 unit of `base` against every currency this app cares about, cached per base for the session. */
export function fetchRates(base: string): Promise<FxRates> {
  let pending = cache.get(base);
  if (!pending) {
    pending = loadRates(base);
    cache.set(base, pending);
  }
  return pending;
}

/**
 * Layers three sources, best first: the ECB's reference rates (via
 * Frankfurter) for everything it publishes; ExchangeRate-API's open feed for
 * the currencies it doesn't (AED, CRC, SAR, VND, EGP, PYG, PEN), or for
 * everything if the ECB can't be reached; and the bundled snapshot underneath
 * for anything still missing. Every rate keeps a note of where it came from.
 */
export async function loadRates(base: string): Promise<FxRates> {
  const snapshot = fallbackFor(base);
  let result: FxRates = { ...snapshot, rates: { ...snapshot.rates, [base]: 1 }, origins: { ...snapshot.origins } };

  const ecb = await fetchEcb(base).catch(() => null);
  if (ecb) {
    result.asOf = ecb.date;
    result.isLive = true;
    for (const [code, rate] of Object.entries(ecb.rates)) {
      result.rates[code] = rate;
      result.origins[code] = { source: 'ecb', asOf: ecb.date };
    }
  }

  const gaps = Object.keys(FALLBACK_GBP_RATES).filter(
    (code) => code !== base && !(code in PEGS) && result.origins[code]?.source !== 'ecb',
  );
  if (gaps.length > 0) {
    const open = await fetchExchangeRateApi(base).catch(() => null);
    if (open) {
      result.isLive = true;
      for (const code of gaps) {
        const rate = open.rates[code];
        if (rate === undefined) continue;
        result.rates[code] = rate;
        result.origins[code] = { source: 'exchangerate-api', asOf: open.date };
      }
      if (!ecb) result.asOf = open.date;
    }
  }

  result = withPegs(result);
  return result;
}

async function fetchEcb(base: string): Promise<{ date: string; rates: Record<string, number> }> {
  // frankfurter.app was retired (sunset 2026-05-18) in favour of frankfurter.dev; same v1 response shape.
  const res = await fetch(`https://api.frankfurter.dev/v1/latest?from=${encodeURIComponent(base)}`);
  if (!res.ok) throw new Error(`Frankfurter responded ${res.status}`);
  return (await res.json()) as { date: string; rates: Record<string, number> };
}

/**
 * ExchangeRate-API's free open endpoint: no key, updated once a day, and its
 * terms ask for a visible "Rates By Exchange Rate API" link wherever its
 * rates are used, which the footer and the per-country rate note carry.
 */
async function fetchExchangeRateApi(base: string): Promise<{ date: string; rates: Record<string, number> }> {
  const res = await fetch(`https://open.er-api.com/v6/latest/${encodeURIComponent(base)}`);
  if (!res.ok) throw new Error(`ExchangeRate-API responded ${res.status}`);
  const data = (await res.json()) as { result: string; time_last_update_unix: number; rates: Record<string, number> };
  if (data.result !== 'success') throw new Error('ExchangeRate-API returned an error');
  return { date: new Date(data.time_last_update_unix * 1000).toISOString().slice(0, 10), rates: data.rates };
}

/** Derives a snapshot quote for any base by pivoting through the GBP snapshot. */
export function fallbackFor(base: string): FxRates {
  const origin: RateOrigin = { source: 'snapshot', asOf: FALLBACK_ASOF };
  const baseToGbp = FALLBACK_GBP_RATES[base];
  if (!baseToGbp) {
    return { base, rates: { [base]: 1 }, origins: {}, asOf: FALLBACK_ASOF, isLive: false };
  }
  const rates: Record<string, number> = {};
  const origins: Record<string, RateOrigin> = {};
  for (const [code, gbpToCode] of Object.entries(FALLBACK_GBP_RATES)) {
    rates[code] = gbpToCode / baseToGbp;
    if (code !== base) origins[code] = origin;
  }
  return withPegs({ base, rates, origins, asOf: FALLBACK_ASOF, isLive: false });
}

/** Converts an amount quoted in `from` into `to`, using whatever `rates` were fetched for base `from`. */
export function convert(amount: number, to: string, rates: FxRates): number {
  if (to === rates.base) return amount;
  const rate = rates.rates[to];
  return rate === undefined ? Number.NaN : amount * rate;
}

/** The inverse: takes an amount already quoted in `from` back into the base currency. */
export function convertToBase(amount: number, from: string, rates: FxRates): number {
  if (from === rates.base) return amount;
  const rate = rates.rates[from];
  return rate === undefined || rate === 0 ? Number.NaN : amount / rate;
}
